import { useEffect, useMemo } from "react";
import { useAuth } from "../auth/authContext.tsx";
import { useTheme } from "../hooks/useTheme.ts";
import { getBrandTitle } from "../api/client.ts";
import { rememberBrand, resolveBrand, resolveEndpointPin, type ResolvedBrand } from "./brand.ts";

/** The signed-in endpoint's id. Taken from the session rather than
 *  `activeEndpoint`, which stays null until the endpoints list has loaded. */
function useActiveEndpointId(): number | null {
  const { session } = useAuth();
  return session?.ActiveEndPointID ?? null;
}

/**
 * Paints the cockpit in the active endpoint's own brand.
 *
 * ThemeProvider sits outside AuthProvider and cannot see the endpoint, so the
 * brand is resolved here and pushed up. It becomes the *derived* pack, which
 * never outranks an explicit SuperAdmin pick and is not kept as a preference —
 * a Maikoz endpoint must not leave the whole cockpit teal after switching back.
 *
 * Renders nothing; mount once inside AuthProvider.
 */
export function BrandSync() {
  const endpointId = useActiveEndpointId();
  const serverBrand = useAuth().cache?.EndpointBrand;
  const { setDerivedThemePack } = useTheme();

  useEffect(() => {
    // Only act once an endpoint is actually known. Before that "no brand" means
    // "not signed in yet", and acting on it would undo the first-frame hint and
    // repaint the cockpit twice on every load. Signed out, the last tenant's
    // brand simply stays up — which is the one the user is signing back into.
    if (!endpointId) return;
    const brand = resolveEndpointPin(serverBrand, endpointId);
    setDerivedThemePack(brand);
    rememberBrand(brand);
  }, [endpointId, serverBrand, setDerivedThemePack]);

  return null;
}

/** The resolved brand without the assistant; components read it through useBrand. */
export function useResolvedBrand(): ResolvedBrand {
  const endpointId = useActiveEndpointId();
  const serverBrand = useAuth().cache?.EndpointBrand;
  const { pickedThemePack, hostThemePack } = useTheme();
  return useMemo(
    () => resolveBrand({ picked: pickedThemePack, endpointId, serverBrand, hostPack: hostThemePack, hostTitle: getBrandTitle() }),
    [pickedThemePack, endpointId, serverBrand, hostThemePack],
  );
}
